import React, { FC, useEffect, useState } from "react";
import {
  FieldErrors,
  FieldValues,
  UseFormRegister,
  UseFormWatch,
} from "react-hook-form";
import { IAccommodationDataType, IFeeDataType } from "../../types/IFormData";
import { ArrowIcon } from "../ArrowIcon";
import { AccommodationTypeDto } from "../../types/contracts";
import {FormField} from "../../types/enums";

interface SelectFormComponentProps {
  register: UseFormRegister<FieldValues>;
  errors: FieldErrors<FieldValues>;
  selectTypeData: IAccommodationDataType | IFeeDataType;
  feeTypes?: { id: string; name: string }[];
  onFeeTypeChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  isLoading?: boolean;
  accommodations?: AccommodationTypeDto[];
  watch: UseFormWatch<FieldValues>;
}

const SelectTypeFormComponent: FC<SelectFormComponentProps> = ({
  register,
  errors,
  selectTypeData,
  feeTypes,
  onFeeTypeChange,
  isLoading,
  accommodations,
  watch,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleClickOutside = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selectedValue = watch(selectTypeData.id);

  const sortedOptions: (AccommodationTypeDto | { id: string; name: string })[] =
    selectTypeData.name === FormField.AccommodationType
      ? accommodations?.slice().sort((a, b) => a.name.localeCompare(b.name)) ??
        []
      : feeTypes?.slice().sort((a, b) => a.name.localeCompare(b.name)) ?? [];

  return (
    <div className="sm:col-span-2">
      <label
        className="block text-sm font-medium mb-1"
        htmlFor={selectTypeData.id}
      >
        {selectTypeData.label}
        {errors[selectTypeData.id]?.message && (
          <span className="ml-1 text-primary-dark font-medium">*</span>
        )}
      </label>
      {isLoading ? (
        <p className="py-4">
          {"loadingText" in selectTypeData && selectTypeData.loadingText}
        </p>
      ) : (
        <>
          <div className="relative w-full">
            <select
              id={selectTypeData.id}
              className="w-full p-2 border border-gray-300 rounded"
              style={{
                appearance: "none",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                paddingRight: "2rem",
              }}
              {...register(selectTypeData.id, {
                required: selectTypeData.requiredErrorMessage,
              })}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                onFeeTypeChange && onFeeTypeChange(e);
                register(selectTypeData.id, {
                  required: selectTypeData.requiredErrorMessage,
                }).onChange(e);
              }}
              onClick={() => toggleDropdown()}
            >
              {!selectedValue && (
                <option value="" hidden>
                  {selectTypeData.placeholder}
                </option>
              )}

              {sortedOptions.map((option) => {
                const isAccommodation =
                  selectTypeData.name === FormField.AccommodationType;
                const label =
                  isAccommodation &&
                  "price" in option &&
                  option.price > 0
                    ? `${option.name} (${option.price}CZK/${
                        "isPricePerNight" in option && option.isPricePerNight
                          ? "noc"
                          : "pobyt"
                      })`
                    : option.name;

                return (
                  <option key={option.id} value={option.id}>
                    {label}
                  </option>
                );
              })}
            </select>

            <div
              className={`absolute inset-y-2 right-2 flex pointer-events-none transition-transform ${
                isOpen ? "rotate-180" : "rotate-0"
              }`}
            >
              <ArrowIcon color="black" />
            </div>
          </div>

          {typeof errors[selectTypeData.id]?.message === "string" && (
            <span className="text-primary-dark text-sm font-medium">{`${
              errors[selectTypeData.id]?.message
            }`}</span>
          )}
        </>
      )}
    </div>
  );
};

export default SelectTypeFormComponent;
